import "core-js/modules/es.array.find.js";
import "core-js/modules/es.object.to-string.js";
import { ChevronDownOutlineIcon, ChevronUpOutlineIcon, SortAscendingOutlineIcon, SortDescendingOutlineIcon } from '@applyboard/ui-icons';
import React from 'react';
import { NativeSelect } from "./Select.appearance.native";
import { StyledSelect, StyledOption } from "./Select.appearance.styled";
import { useId } from "../../utils";
import { SelectContext } from "./Select.utils";
import { useContextOrThrow } from "../../utils/useContextOrThrow";
import { assert } from "../../utils/assert";

/**
 * To ensure consistency of the Select component UI, and so that we don't face major usability issues
 * with the Select component, only arrow-like icons are allowed as trigger icons.
 */
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
export var ALLOWED_TRIGGER_ICONS = [ChevronUpOutlineIcon, ChevronDownOutlineIcon, SortAscendingOutlineIcon, SortDescendingOutlineIcon];
/**
 * A select allows users pick a single value from predefined options.
 * - Only use Select for exclusive selection of 5 to 10 items.
 * - For 1 item use a read only Label and Body pair
 * - For 2 items use a Checkbox, Toggle Switch, Button Group, or Radio Group
 * - For 3 to 6 items use a Button Group or Radio Group
 * - For more than 10 items use an Combobox
 */
export var Select = /*#__PURE__*/React.forwardRef(function Select(_ref, ref) {
  var _ref2;
  var ariaLabel = _ref['aria-label'],
    ariaLabelledBy = _ref['aria-labelledby'],
    _ref$appearance = _ref.appearance,
    appearance = _ref$appearance === void 0 ? 'native' : _ref$appearance,
    children = _ref.children,
    _ref$disabled = _ref.disabled,
    _disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _emphasis = _ref.emphasis,
    helpText = _ref.helpText,
    _ref$iconOnClose = _ref.iconOnClose,
    IconOnClose = _ref$iconOnClose === void 0 ? ChevronDownOutlineIcon : _ref$iconOnClose,
    IconOnOpen = _ref.iconOnOpen,
    loading = _ref.loading,
    _ref$intent = _ref.intent,
    intent = _ref$intent === void 0 ? 'secondary' : _ref$intent,
    invalid = _ref.invalid,
    label = _ref.label,
    labelPosition = _ref.labelPosition,
    _ref$menuAlignment = _ref.menuAlignment,
    menuAlignment = _ref$menuAlignment === void 0 ? 'start' : _ref$menuAlignment,
    _ref$menuWidth = _ref.menuWidth,
    menuWidth = _ref$menuWidth === void 0 ? 'fill' : _ref$menuWidth,
    onBlur = _ref.onBlur,
    onChange = _ref.onChange,
    options = _ref.options,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? 'Select' : _ref$placeholder,
    _ref$placeholderVaria = _ref.placeholderVariant,
    placeholderVariant = _ref$placeholderVaria === void 0 ? 'value' : _ref$placeholderVaria,
    _ref$required = _ref.required,
    required = _ref$required === void 0 ? false : _ref$required,
    _size = _ref.size,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    _variant = _ref.variant,
    enforceNoMissingOption = _ref.enforceNoMissingOption,
    _ref$width = _ref.width,
    width = _ref$width === void 0 ? 'fill' : _ref$width;
  if (options) {
    throw new Error('`options` prop is no longer supported, instead pass options as Select.Option children');
  }
  if (appearance === 'native') {
    if (_emphasis) {
      throw Error('Prop `emphasis` has no effect if Select appearance is native');
    }
    if (_variant) {
      throw Error('Prop `variant` has no effect if Select appearance is native');
    }
    if (IconOnOpen) {
      throw Error('When Select appearance is native, `IconOnOpen` is not available');
    }
    if (_size) {
      throw Error('Prop `size` has no effect if Select appearance is native');
    }
  }
  if (!label && !ariaLabel && !ariaLabelledBy) {
    throw Error('Select component requires either aria-label or aria-labelledby prop when label prop is undefined.');
  }
  if (IconOnClose && !ALLOWED_TRIGGER_ICONS.find(function (icon) {
    return icon === IconOnClose;
  }) || IconOnOpen && !ALLOWED_TRIGGER_ICONS.find(function (icon) {
    return icon === IconOnOpen;
  })) {
    throw Error('Only a subset of icons are allowed as IconOnClose/IconOnOpen. Refer to storybook for the allowed values.');
  }
  var disabled = _disabled || loading;
  var SelectComponent = appearance === 'native' ? NativeSelect : StyledSelect;
  var id = useId();
  var emphasis = (_ref2 = _variant !== null && _variant !== void 0 ? _variant : _emphasis) !== null && _ref2 !== void 0 ? _ref2 : 'outlined';
  var size = _size ? _size : 'md';
  return _jsx(SelectContext.Provider, {
    value: {
      appearance: appearance
    },
    children: _jsx(SelectComponent, {
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      disabled: disabled,
      emphasis: emphasis,
      helpText: helpText,
      iconOnClose: IconOnClose,
      iconOnOpen: IconOnOpen !== null && IconOnOpen !== void 0 ? IconOnOpen : ChevronUpOutlineIcon,
      loading: loading,
      id: id,
      intent: intent,
      invalid: invalid,
      label: label,
      labelPosition: labelPosition,
      menuAlignment: menuAlignment,
      menuWidth: menuWidth,
      onBlur: onBlur,
      onChange: onChange,
      placeholder: placeholder,
      placeholderVariant: placeholderVariant,
      required: required,
      size: size,
      value: value,
      enforceNoMissingOption: enforceNoMissingOption
      // @ts-expect-error Not sure how to fix this.  It should be fine at runtime
      ,
      ref: ref,
      width: width,
      children: children
    })
  });
});
Select.Option = /*#__PURE__*/React.forwardRef(function SelectOption(props, ref) {
  var context = useContextOrThrow(SelectContext, 'Select.Option can only be used within Select');
  if (context.appearance === 'native') {
    assert(typeof props['aria-controls'] === 'undefined', 'aria-controls prop is not supported in native appearance');
    assert(typeof props['aria-haspopup'] === 'undefined', 'aria-haspopup prop is not supported in native appearance');
    assert(typeof props.onClick === 'undefined', 'onClick prop is not supported in native appearance');
    assert(typeof props.intent === 'undefined', 'intent prop is not supported in native appearance');
    return _jsx("option", {
      disabled: props.disabled,
      value: props.value
      // @ts-expect-error Not sure how to fix this.  It should be fine at runtime
      ,
      ref: ref,
      children: props.label
    });
  }
  return _jsx(StyledOption, {
    intent: props.intent,
    disabled: props.disabled,
    label: props.label,
    value: props.value,
    icon: props.icon,
    "aria-controls": props['aria-controls'],
    "aria-haspopup": props['aria-haspopup'],
    onClick: props.onClick
    // @ts-expect-error Not sure how to fix this.  It should be fine at runtime
    ,
    ref: ref
  });
});